import React, { useState, useEffect } from 'react';
import { Building2 } from 'lucide-react';
import { getCompanies } from '../../../api/wholesale/companies';
import ManufacturingCompanyStatement from '../../../components/wholesale/ManufacturingCompanyStatement';

const GeneralStatementOfAccountPage = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('all');
  const [loading, setLoading] = useState(true);
  const [includeStandaloneExpenses, setIncludeStandaloneExpenses] = useState(true);
  const [includeSalaries, setIncludeSalaries] = useState(true);
  const [includeOrderExpenses, setIncludeOrderExpenses] = useState(true);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await getCompanies({ limit: 1000 });
        if (response.data) {
          setCompanies(response.data);
        }
      } catch (err) {
        console.error('Error fetching companies:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  const selectedCompanyName = Array.isArray(selectedCompanyId)
    ? selectedCompanyId.length === 0
      ? 'Standalone / Filter Items Only'
      : selectedCompanyId
          .map((id) => companies.find((c) => c.id.toString() === id.toString())?.company_name)
          .filter(Boolean)
          .join(', ')
    : selectedCompanyId === 'all_general' || selectedCompanyId === 'all'
      ? 'General (Unified) Statement'
      : selectedCompanyId === 'all_manufacturing'
        ? 'All Manufacturing Combined'
        : companies.find((c) => c.id.toString() === selectedCompanyId.toString())?.company_name ||
          'Selected Factory';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">General Statement of Account</h1>
          <p className="text-gray-500 mt-1">
            Cross-company unified ledger and specific financial statements
          </p>
        </div>
      </div>

      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Report Type / Preset
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCompanyId('all_general')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedCompanyId === 'all_general' || selectedCompanyId === 'all'
                    ? 'bg-accent-gold text-primary-black shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                1. General Account (Combined + Account Balance)
              </button>
              <button
                onClick={() => setSelectedCompanyId([])}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  Array.isArray(selectedCompanyId) && selectedCompanyId.length === 0
                    ? 'bg-accent-gold text-primary-black shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                2. Standalone / Filter Items Only (No Factory)
              </button>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-100">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            3. Individual Manufacturing Companies (Select one or more)
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {companies.map((company) => {
              const isChecked =
                Array.isArray(selectedCompanyId) && selectedCompanyId.includes(company.id);
              return (
                <label
                  key={company.id}
                  className={`flex items-center gap-2.5 px-3 py-2 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer select-none ${
                    isChecked ? 'border-accent-gold bg-accent-gold/5' : 'border-gray-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      let currentIds = Array.isArray(selectedCompanyId)
                        ? [...selectedCompanyId]
                        : [];
                      if (e.target.checked) {
                        currentIds.push(company.id);
                      } else {
                        currentIds = currentIds.filter((id) => id !== company.id);
                      }
                      setSelectedCompanyId(currentIds);
                    }}
                    className="w-4 h-4 text-accent-gold border-gray-300 rounded focus:ring-accent-gold"
                  />
                  <span className="text-sm font-medium text-gray-700">{company.company_name}</span>
                </label>
              );
            })}
          </div>
          {Array.isArray(selectedCompanyId) && selectedCompanyId.length > 0 && (
            <button
              onClick={() => setSelectedCompanyId([])}
              className="mt-3 text-xs text-red-600 hover:text-red-700 font-medium"
            >
              Clear selected companies
            </button>
          )}
        </div>
        <div className="pt-4 border-t border-gray-100">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            4. Select / Filter Statement Items
          </label>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2.5 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeStandaloneExpenses}
                onChange={(e) => setIncludeStandaloneExpenses(e.target.checked)}
                className="w-4 h-4 text-accent-gold border-gray-300 rounded focus:ring-accent-gold"
              />
              <span className="text-sm font-medium text-gray-700">
                Expenses (not linked to order)
              </span>
            </label>

            <label className="flex items-center gap-2.5 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeSalaries}
                onChange={(e) => setIncludeSalaries(e.target.checked)}
                className="w-4 h-4 text-accent-gold border-gray-300 rounded focus:ring-accent-gold"
              />
              <span className="text-sm font-medium text-gray-700">Salaries & Payroll</span>
            </label>

            <label className="flex items-center gap-2.5 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeOrderExpenses}
                onChange={(e) => setIncludeOrderExpenses(e.target.checked)}
                className="w-4 h-4 text-accent-gold border-gray-300 rounded focus:ring-accent-gold"
              />
              <span className="text-sm font-medium text-gray-700">Expenses linked to orders</span>
            </label>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <ManufacturingCompanyStatement
          companyId={selectedCompanyId}
          companyName={selectedCompanyName}
          includeStandaloneExpenses={includeStandaloneExpenses}
          includeSalaries={includeSalaries}
          includeOrderExpenses={includeOrderExpenses}
        />
      </div>
    </div>
  );
};

export default GeneralStatementOfAccountPage;
